/**
 * Owns every selection rule and every count string in the bulk-invite flow
 * (`Chats` and `Pools` source tabs, ISSUE-197): a candidate starts selected,
 * an unselect removes it, and the live confirm/sent copy is derived here so
 * it is testable without rendering anything.
 *
 * A deliberate copy of `apps/mobile/src/pools/invite/inviteBulkSelection.ts`,
 * not a shared package. Extracting it would rewrite imports inside
 * `apps/mobile`, which the 2026-09-06 mobile platform ruling freezes. Keep
 * the two files identical so the confirm string cannot drift between the two
 * surfaces; the strings live here rather than in `inviteSheetCopy.ts` because
 * they interpolate a live count and a source name.
 */

export type typeInviteCandidate = {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
};

export type typeInviteSelectionRow = {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  isSelected: boolean;
};

export type typeInviteSelection = {
  rows: typeInviteSelectionRow[];
  selectedUserIds: string[];
  selectedCount: number;
  canSend: boolean;
};

export function buildInviteSelection(input: {
  candidates: readonly typeInviteCandidate[];
  unselectedUserIds: readonly string[];
}): typeInviteSelection {
  const rows: typeInviteSelectionRow[] = input.candidates.map((candidate) => ({
    userId: candidate.userId,
    displayName: candidate.displayName,
    avatarUrl: candidate.avatarUrl,
    isSelected: !input.unselectedUserIds.includes(candidate.userId),
  }));
  const selectedUserIds = rows
    .filter((row) => row.isSelected)
    .map((row) => row.userId);

  return {
    rows,
    selectedUserIds,
    selectedCount: selectedUserIds.length,
    canSend: selectedUserIds.length > 0,
  };
}

export function toggleInviteCandidate(input: {
  unselectedUserIds: readonly string[];
  userId: string;
}): string[] {
  if (input.unselectedUserIds.includes(input.userId)) {
    return input.unselectedUserIds.filter((id) => id !== input.userId);
  }
  return [...input.unselectedUserIds, input.userId];
}

export function inviteConfirmHeadline(input: {
  selectedCount: number;
  sourceName: string;
}): string {
  if (input.selectedCount === 0) {
    return "Pick at least one person to invite.";
  }
  if (input.selectedCount === 1) {
    return `Invite 1 person from ${input.sourceName}?`;
  }
  return `Invite ${input.selectedCount} people from ${input.sourceName}?`;
}

export function inviteSentNote(input: { sentCount: number }): string {
  if (input.sentCount === 0) {
    return "No invites went out.";
  }
  if (input.sentCount === 1) {
    return "Sent 1 invite.";
  }
  return `Sent ${input.sentCount} invites.`;
}
